import React, { useEffect, useState } from "react";
import { ArrowLeftRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getCategories } from "@/lib/api";
import Header from "@/components/Header";
import ProductPicker from "@/components/ProductPicker";
import ComparisonView from "@/components/ComparisonView";
import UploadDialog from "@/components/UploadDialog";
import ChatDrawer from "@/components/ChatDrawer";
import ManageProducts from "@/components/ManageProducts";
import QuoteBuilder from "@/components/QuoteBuilder";
import ContactBar from "@/components/ContactBar";
import MeasureView from "@/components/MeasureView";
import SpecsAnalysisView from "@/components/SpecsAnalysisView";
import StratigraphyConfigurator from "@/components/StratigraphyConfigurator";
import { ScanLine, FilePlus2 } from "lucide-react";

export default function Home() {
  const { t, view, setView } = useApp();
  const [categories, setCategories] = useState([]);
  const [productA, setProductA] = useState(null);
  const [productB, setProductB] = useState(null);
  const [ctxA, setCtxA] = useState({ category: "", subcategory: "" });
  const [suggestedB, setSuggestedB] = useState(new Set());
  const [refreshKey, setRefreshKey] = useState(0);
  const [upload, setUpload] = useState({ open: false, subcategory: "colle_piastrelle", side: "a" });

  useEffect(() => {
    getCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const bothSelected = productA && productB;
  const anySelected = productA || productB;
  const comparedIds = [productA?.id, productB?.id].filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="mx-auto max-w-[1400px] px-4 py-8 pb-28 sm:px-6">
        {view === "manage" ? (
          <ManageProducts />
        ) : view === "measure" ? (
          <MeasureView />
        ) : view === "specs" ? (
          <SpecsAnalysisView />
        ) : view === "systems" ? (
          <StratigraphyConfigurator />
        ) : view === "quote" ? (
          <QuoteBuilder
            categories={categories}
            refreshKey={refreshKey}
            onOpenUpload={(sc) => setUpload({ open: true, subcategory: sc || "colle_piastrelle", side: "a" })}
          />
        ) : (
          <>
            <div className="mb-8 max-w-2xl">
              <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl">
                {t("compare")}.
              </h1>
              <p className="mt-3 text-base text-muted-foreground">{t("subtitle")}</p>
            </div>

            <div className="relative grid grid-cols-1 gap-4 lg:grid-cols-2">
              <ProductPicker
                side="a" categories={categories} value={productA} refreshKey={refreshKey}
                onSelect={setProductA}
                onContextChange={setCtxA}
                onOpenUpload={(sc) => setUpload({ open: true, subcategory: sc, side: "a" })}
              />
              <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 lg:block">
                <div className="flex h-10 w-10 items-center justify-center border border-border bg-background text-muted-foreground">
                  <ArrowLeftRight className="h-4 w-4" />
                </div>
              </div>
              <ProductPicker
                side="b" categories={categories} value={productB} refreshKey={refreshKey}
                onSelect={setProductB}
                lockCategory={ctxA.category}
                lockSubcategory={ctxA.subcategory}
                refProductId={productA?.id}
                onSuggested={(ids) => setSuggestedB(ids)}
                onOpenUpload={(sc) => setUpload({ open: true, subcategory: sc, side: "b" })}
              />
            </div>

            <div className="mt-8">
              {anySelected ? (
                <ComparisonView productA={productA} productB={productB} onPickB={setProductB} matchedB={productB && suggestedB?.has?.(productB.id)} />
              ) : (
                <EmptyState t={t} />
              )}
            </div>
          </>
        )}
      </main>

      {view !== "manage" && view !== "measure" && <ContactBar />}

      <footer className="border-t border-border px-4 py-3 text-center sm:px-6" data-testid="disclaimer-footer">
        <p className="text-[10px] leading-snug text-muted-foreground/60">{t("disclaimerShort")}</p>
        <p className="mt-1 text-xs text-muted-foreground/70" data-testid="copyright-footer">{t("copyright")}</p>
      </footer>

      {view === "compare" && <div aria-hidden className="h-20" />}
      {view === "quote" && <div aria-hidden className="h-20" />}

      <UploadDialog
        open={upload.open}
        subcategory={upload.subcategory}
        onClose={() => setUpload((u) => ({ ...u, open: false }))}
        onSaved={(product) => {
          setRefreshKey((k) => k + 1);
          if (upload.global) return;
          if (upload.side === "b") setProductB(product);
          else setProductA(product);
        }}
      />

      {/* Bottom-left FABs — upload datasheet, and (on compare/quote) the measure tool side by side */}
      <div className="fixed bottom-5 left-4 z-40 flex items-center gap-3 sm:left-6">
        <button
          data-testid="add-datasheet-fab"
          onClick={() => setUpload({ open: true, subcategory: null, side: null, global: true })}
          title={t("fabAddDatasheet")}
          className="flex items-center gap-2 rounded-full border border-primary bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/30 transition-transform hover:scale-105 active:scale-95"
        >
          <FilePlus2 className="h-5 w-5 shrink-0" />
          <span className="hidden md:inline">{t("fabAddDatasheet")}</span>
        </button>

        {(view === "compare" || view === "quote") && (
          <button
            data-testid="measure-fab"
            onClick={() => setView("measure")}
            className="flex items-center gap-2 rounded-full border border-primary bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95"
          >
            <ScanLine className="h-5 w-5" />
            <span className="hidden sm:inline">{t("measureFab")}</span>
          </button>
        )}
      </div>

      {view !== "manage" && <ChatDrawer comparedIds={comparedIds} />}
    </div>
  );
}

function EmptyState({ t }) {
  return (
    <div
      data-testid="empty-state"
      className="flex flex-col items-center justify-center border border-dashed border-border bg-surface py-20 text-center"
    >
      <div className="mb-4 flex h-14 w-14 items-center justify-center border border-border bg-background">
        <ArrowLeftRight className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="font-display text-xl font-bold tracking-tight">{t("emptyTitle")}</h3>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">{t("emptyBody")}</p>
    </div>
  );
}
